import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Page, PageHeader } from '@/components/shell'
import { AnnouncementPreview, type AnnouncementContent } from '@/components/announcement-preview'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { api, type AnnouncementList, type AnnouncementLogs } from '@/lib/api'
import { useCan } from '@/lib/session'
import styles from './announcements.module.css'

export const Route = createFileRoute('/dashboard/announcements')({ component: AnnouncementsPage })
type Row = AnnouncementList['items'][number]
type Form = AnnouncementContent & { id?: string; version?: number }
type Operation =
  { kind: 'save'; form: Form } | { kind: 'status'; row: Row } | { kind: 'delete'; row: Row }
const blank = (): Form => ({
  title: { zh: '', en: '' },
  subtitle: { zh: '', en: '' },
  linkText: { zh: '', en: '' },
  link: '',
  enabled: false,
  dismissible: true,
})
const edit = (row: Row): Form => ({
  id: row.id,
  version: row.version,
  title: { ...row.title },
  subtitle: { ...row.subtitle },
  linkText: { ...row.linkText },
  link: row.link,
  enabled: row.enabled,
  dismissible: row.dismissible,
})
const limits = {
  zh: { title: 40, subtitle: 60, linkText: 12 },
  en: { title: 60, subtitle: 90, linkText: 18 },
}
const actionLabels = {
  create: '新建',
  update: '编辑',
  enable: '启用',
  disable: '停用',
  delete: '删除',
}
const time = (value: string) => new Date(value).toLocaleString('zh-CN', { hour12: false })
const errorMessage = (error: unknown) => (error instanceof Error ? error.message : '请稍后重试')

function Pages({
  page,
  total,
  onChange,
}: {
  page: number
  total: number
  onChange: (page: number) => void
}) {
  return (
    <div className="flex items-center justify-end gap-3 py-3 text-xs text-muted-foreground">
      <span>
        共 {total} 条 · 第 {page} / {Math.max(1, Math.ceil(total / 20))} 页
      </span>
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        上一页
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={page * 20 >= total}
        onClick={() => onChange(page + 1)}
      >
        下一页
      </Button>
    </div>
  )
}

function AnnouncementsPage() {
  const canManage = useCan()('announcement', 'manage')
  const qc = useQueryClient()
  const [page, setPage] = useState(1),
    [logPage, setLogPage] = useState(1)
  const [logId, setLogId] = useState(''),
    [logAction, setLogAction] = useState('')
  const [logIdInput, setLogIdInput] = useState('')
  const [logActionInput, setLogActionInput] = useState('')
  const [form, setForm] = useState<Form | null>(null),
    [saved, setSaved] = useState('')
  const [locale, setLocale] = useState<'zh' | 'en'>('zh')
  const [deleting, setDeleting] = useState<Row | null>(null)
  const [snapshot, setSnapshot] = useState<AnnouncementLogs['items'][number] | null>(null)
  const [replacement, setReplacement] = useState<Operation | null>(null)
  const dirty = form !== null && JSON.stringify(form) !== saved
  const query = useQuery({
    queryKey: ['announcements', page],
    queryFn: () => api.get<AnnouncementList>('/announcements', { page }),
    retry: false,
  })
  const logs = useQuery({
    queryKey: ['announcement-logs', logPage, logId, logAction],
    queryFn: () =>
      api.get<AnnouncementLogs>('/announcements/logs', {
        page: logPage,
        announcementId: logId,
        action: logAction,
      }),
    retry: false,
  })
  const applyLogFilter = (id: string, action: string) => {
    if (logId === id && logAction === action && logPage === 1) void logs.refetch()
    else {
      setLogId(id)
      setLogAction(action)
      setLogPage(1)
    }
  }
  const lastUpdatedAt = query.data?.current?.updatedAt ?? query.data?.items[0]?.updatedAt
  const load = (next: Form) => {
    setForm(next)
    setSaved(JSON.stringify(next))
  }
  useEffect(() => {
    if (!form && query.data?.items[0]) load(edit(query.data.items[0]))
    const latest = query.data?.items.find((row) => row.id === form?.id)
    if (!dirty && latest && latest.version > (form?.version ?? 0)) load(edit(latest))
  }, [query.data, form, dirty])
  const select = (next: Form) => {
    if (!dirty || window.confirm('当前修改尚未保存，是否放弃修改？')) load(next)
  }
  const mutation = useMutation({
    mutationFn: (op: Operation) => {
      if (op.kind === 'save')
        return op.form.id
          ? api.put<Row>(`/announcements/${op.form.id}`, op.form)
          : api.post<Row>('/announcements', op.form)
      if (op.kind === 'status')
        return api.patch<Row>(`/announcements/${op.row.id}/status`, {
          enabled: !op.row.enabled,
          version: op.row.version,
        })
      return api.del<Row>(`/announcements/${op.row.id}?version=${op.row.version}`)
    },
    onSuccess: async (row, op) => {
      if (op.kind === 'save' || (op.kind === 'status' && form?.id === row.id && !dirty))
        load(edit(row))
      if (op.kind === 'delete') {
        setDeleting(null)
        if (form?.id === row.id) load(blank())
        if (query.data?.items.length === 1 && page > 1) setPage(page - 1)
      }
      setReplacement(null)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['announcements'] }),
        qc.invalidateQueries({ queryKey: ['announcement-logs'] }),
      ])
      toast.success(
        op.kind === 'delete' ? '公告已删除，操作记录已保留' : '公告已保存，前台最多 5 分钟内更新',
      )
    },
    onError: (error) => {
      toast.error('操作失败', { description: errorMessage(error) })
      void qc.invalidateQueries({ queryKey: ['announcements'] })
    },
  })
  const save = (next: Form) => {
    if (next.enabled && query.data?.current && query.data.current.id !== next.id)
      setReplacement({ kind: 'save', form: next })
    else mutation.mutate({ kind: 'save', form: next })
  }
  const toggle = (row: Row) => {
    if (!row.enabled && query.data?.current && query.data.current.id !== row.id)
      setReplacement({ kind: 'status', row })
    else mutation.mutate({ kind: 'status', row })
  }

  return (
    <div className={styles.page}>
    <Page>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>SYSTEM ANNOUNCEMENT</span>
      <PageHeader
        title="系统公告管理"
        description="配置全站顶部公告；同时仅一条启用，保存后最多 5 分钟内更新。"
      />
      </div>
      <div className={styles.stats}>
        {[
          ['当前状态', query.data?.current ? '启用' : '停用'],
          ['公告总数', String(query.data?.total ?? '—')],
          ['最近更新', lastUpdatedAt ? time(lastUpdatedAt) : '暂无'],
        ].map(([label, value]) => (
          <Card key={label} className="py-0">
            <CardContent className="py-5">
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="mt-2 break-words text-lg font-semibold">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className={styles.records}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>公告记录</CardTitle>
          <div className="flex items-center gap-2">
          <Button disabled={!canManage || mutation.isPending} onClick={() => select(blank())}>
            <Plus />
            新建公告
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch()
              void logs.refetch()
            }}
          >
            <RefreshCw />
            刷新
          </Button>
          </div>
        </CardHeader>
        <CardContent>
          {query.isPending && <p>正在加载公告…</p>}
          {query.isError && (
            <p role="alert" className="text-destructive">
              {errorMessage(query.error)}
            </p>
          )}
          {query.data?.items.length === 0 && (
            <p className="py-5 text-muted-foreground">暂无公告，点击新建公告开始配置。</p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-3">主文案</th>
                  <th className="p-3">状态</th>
                  <th className="p-3">可关闭</th>
                  <th className="p-3">更新时间</th>
                  <th className="p-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {query.data?.items.map((row) => (
                  <tr key={row.id} aria-selected={row.id === form?.id} className={`border-b ${row.id === form?.id ? 'bg-cyan/5' : ''}`}>
                    <td className="max-w-xs p-3">
                      <div className="truncate font-medium">{row.title.zh || row.title.en}</div>
                      <div
                        className="mt-1 truncate font-mono text-xs text-muted-foreground"
                        title={row.id}
                      >
                        {row.id}
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={row.enabled ? 'default' : 'secondary'}>
                        {row.enabled ? '启用' : '停用'}
                      </Badge>
                    </td>
                    <td className="p-3 whitespace-nowrap">{row.dismissible ? '允许' : '不允许'}</td>
                    <td className="p-3 whitespace-nowrap font-mono text-xs">
                      {time(row.updatedAt)}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={mutation.isPending}
                          onClick={() => select(edit(row))}
                        >
                          编辑
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!canManage || mutation.isPending}
                          onClick={() => toggle(row)}
                        >
                          {row.enabled ? '停用' : '启用'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={!canManage || mutation.isPending}
                          onClick={() => setDeleting(row)}
                        >
                          删除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.data && (
            <Pages
              page={page}
              total={query.data.total}
              onChange={(next) => {
                if (!dirty || window.confirm('当前修改尚未保存，是否放弃修改？')) {
                  setPage(next)
                  setForm(null)
                }
              }}
            />
          )}
        </CardContent>
      </Card>
      {form && (
        <div className={styles.work}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{form.id ? '编辑公告' : '新建公告'}</CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={locale === 'zh' ? 'default' : 'outline'}
                  onClick={() => setLocale('zh')}
                >
                  中文
                </Button>
                <Button
                  size="sm"
                  variant={locale === 'en' ? 'default' : 'outline'}
                  onClick={() => setLocale('en')}
                >
                  EN
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault()
                  save(form)
                }}
              >
                <fieldset disabled={!canManage || mutation.isPending} className={styles.fields}>
                  {(['title', 'subtitle', 'linkText'] as const).map((key, index) => (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor={`announcement-${key}`}>
                          {['主文案', '副文案', '按钮文字'][index]}（
                          {locale === 'zh' ? '中文' : '英文'}）
                        </Label>
                        <span className="font-mono text-xs text-muted-foreground">
                          {form[key][locale].length}/{limits[locale][key]}
                        </span>
                      </div>
                      <Input
                        id={`announcement-${key}`}
                        maxLength={limits[locale][key]}
                        value={form[key][locale]}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            [key]: { ...form[key], [locale]: event.target.value },
                          })
                        }
                      />
                    </div>
                  ))}
                  <div className={styles.linkField}>
                    <Label htmlFor="announcement-link">跳转链接</Label>
                    <Input
                      id="announcement-link"
                      value={form.link}
                      maxLength={2048}
                      placeholder="https://… 或 /docs；可留空"
                      onChange={(event) => setForm({ ...form, link: event.target.value })}
                    />
                  </div>
                  <div className={styles.idField}>
                    <Label>公告 ID（系统生成）</Label>
                    <div className="break-all rounded-lg border bg-muted/30 p-3 font-mono text-xs text-muted-foreground">
                      {form.id || '保存时自动生成'}
                    </div>
                  </div>
                  <div className={styles.setting}>
                    <div>
                      <Label htmlFor="announcement-enabled">启用公告</Label>
                      <p className="mt-1 text-xs text-muted-foreground">开启后替换当前公告</p>
                    </div>
                    <Switch
                      id="announcement-enabled"
                      checked={form.enabled}
                      onCheckedChange={(enabled) => setForm({ ...form, enabled })}
                    />
                  </div>
                  <div className={styles.setting}>
                    <div>
                      <Label htmlFor="announcement-dismissible">允许用户关闭</Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        当天隐藏，按用户本地日期隔天重现
                      </p>
                    </div>
                    <Switch
                      id="announcement-dismissible"
                      checked={form.dismissible}
                      onCheckedChange={(dismissible) => setForm({ ...form, dismissible })}
                    />
                  </div>
                </fieldset>
                <p className="text-xs text-muted-foreground">
                  公告配色跟随前台深浅主题，强调色统一为 IGNIX 粉。
                  编辑保留原 ID；希望已关闭的用户再次看到，请新建公告。
                </p>
                <div className="flex justify-end gap-2">
                  {form.id && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={mutation.isPending}
                      onClick={() => {
                        const row = query.data?.items.find((row) => row.id === form.id)
                        if (row) select(edit(row))
                      }}
                    >
                      重新载入
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={
                      !canManage ||
                      mutation.isPending ||
                      (!form.title.zh.trim() && !form.title.en.trim())
                    }
                  >
                    {mutation.isPending ? '保存中…' : '保存公告'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>实时预览</CardTitle>
            </CardHeader>
            <CardContent>
              <AnnouncementPreview value={form} record={query.data?.items.find(row => row.id === form.id)} />
            </CardContent>
          </Card>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle>操作记录</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className={styles.logFilters}
            aria-label="操作记录筛选"
            onSubmit={(event) => {
              event.preventDefault()
              applyLogFilter(logIdInput.trim(), logActionInput)
            }}
          >
            <Input
              className="sm:max-w-xs"
              aria-label="按公告 ID 筛选"
              placeholder="完整公告 ID"
              value={logIdInput}
              onChange={(event) => setLogIdInput(event.target.value)}
            />
            <select
              className="rounded-md border bg-background px-3 text-sm"
              aria-label="按操作类型筛选"
              value={logActionInput}
              onChange={(event) => setLogActionInput(event.target.value)}
            >
              <option value="">全部操作</option>
              {Object.entries(actionLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <Button type="submit" variant="outline" size="sm" disabled={logs.isFetching}>
              {logs.isFetching ? '筛选中…' : '筛选'}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={logs.isFetching}
              onClick={() => {
                setLogIdInput('')
                setLogActionInput('')
                applyLogFilter('', '')
              }}>
              重置
            </Button>
          </form>
          {logs.isPending && <p>正在加载操作记录…</p>}
          {logs.isError && (
            <p role="alert" className="text-destructive">
              {errorMessage(logs.error)}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" aria-label="公告操作记录">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-3">时间</th>
                  <th className="p-3">操作</th>
                  <th className="p-3">公告 ID</th>
                  <th className="p-3">操作人</th>
                  <th className="p-3">内容快照</th>
                </tr>
              </thead>
              <tbody>
                {logs.data?.items.map((log) => (
                  <tr key={log.id} className="border-b">
                    <td className="p-3 whitespace-nowrap font-mono text-xs">
                      {time(log.createdAt)}
                    </td>
                    <td className="p-3">{actionLabels[log.action]}</td>
                    <td className="p-3 font-mono text-xs">{log.announcementId}</td>
                    <td className="p-3">{log.actorUsername}</td>
                    <td className="p-3">
                      <Button size="sm" variant="ghost" onClick={() => setSnapshot(log)}>
                        查看快照
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {logs.data?.items.length === 0 && (
            <p className="py-5 text-muted-foreground">{logId || logAction ? '没有符合筛选条件的操作记录。' : '暂无操作记录。'}</p>
          )}
          {logs.data && <Pages page={logPage} total={logs.data.total} onChange={setLogPage} />}
        </CardContent>
      </Card>
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => !open && !mutation.isPending && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除公告？</DialogTitle>
            <DialogDescription>
              删除后前台不再展示，操作记录和内容快照保留。{deleting?.title.zh || deleting?.title.en}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setDeleting(null)}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              disabled={mutation.isPending}
              onClick={() => deleting && mutation.mutate({ kind: 'delete', row: deleting })}
            >
              确认删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!replacement}
        onOpenChange={(open) => !open && !mutation.isPending && setReplacement(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>替换当前公告？</DialogTitle>
            <DialogDescription>
              当前启用的「{query.data?.current?.title.zh || query.data?.current?.title.en}
              」将自动停用，两条公告的变更均会留下操作记录。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setReplacement(null)}
            >
              取消
            </Button>
            <Button
              disabled={mutation.isPending}
              onClick={() => replacement && mutation.mutate(replacement)}
            >
              确认替换
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!snapshot} onOpenChange={(open) => !open && setSnapshot(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>操作内容快照</DialogTitle>
            <DialogDescription>
              {snapshot &&
                `${actionLabels[snapshot.action]} · ${snapshot.actorUsername} · ${time(snapshot.createdAt)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {(['before', 'after'] as const).map((key) => (
              <div key={key}>
                <h3 className="mb-2 font-medium">{key === 'before' ? '变更前' : '变更后'}</h3>
                {snapshot?.[key] ? (
                  <div className="space-y-2 text-sm">
                    <p>中文：{snapshot[key].title.zh || '—'}</p>
                    <p>英文：{snapshot[key].title.en || '—'}</p>
                    <p>
                      状态：
                      {snapshot[key].deletedAt ? '已删除' : snapshot[key].enabled ? '启用' : '停用'}
                    </p>
                    <details>
                      <summary className="cursor-pointer text-cyan">全部字段</summary>
                      <pre className="mt-2 overflow-auto rounded-lg bg-muted p-3 text-xs">
                        {JSON.stringify(snapshot[key], null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">无</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Page>
    </div>
  )
}
